/**
 * The editor menu bar (reference 02): File, Edit, Window, Level, Help. The
 * reference's Build menu is left out: it would have nothing in it.
 *
 * Keyboard accessible like a desktop menu bar: Left/Right move between menus,
 * Up/Down move through items, Enter activates, Esc closes. Once a menu is open,
 * hovering another title switches to it, as in Unreal.
 *
 * Every item ends in a command dispatch or a plain link. Menus never mutate
 * state themselves.
 */
import { useCallback, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useEditor } from "../EditorContext";
import { useSelection, useThemeChoice, useUndoState, useWorldSnapshot } from "../hooks/editorHooks";
import { entityEditItems, useEntityEdits } from "../selection/entityEdits";
import { MenuPopup } from "../ui/Menu";
import type { MenuItem } from "../ui/Menu";
import { useDismissOnOutsideClick } from "../ui/useDismissOnOutsideClick";
import { ThemeToggle } from "./ThemeToggle";
import "./menuBar.css";
import { SYSTEM_THEME } from "../theme/themeBoot";
import { useStore } from "../hooks/useStore";

export type { MenuItem };

export interface Menu {
  readonly label: string;
  readonly items: readonly MenuItem[];
}

export interface MenuBarProps {
  /** Extra items contributed by features (Window > Reset Layout, Help > Tour, …). */
  readonly extraItems?: Readonly<Record<string, readonly MenuItem[]>>;
}

export function MenuBar({ extraItems = {} }: MenuBarProps) {
  const menus = useMenus(extraItems);

  return (
    <header className="kn-menubar">
      <span className="kn-menubar__brand">
        <span className="kn-menubar__mark" aria-hidden="true" />
        kn.engine
      </span>
      <MenuList menus={menus} />
      <span className="kn-menubar__spacer" />
      <OpenLevel />
      <ThemeToggle />
    </header>
  );
}

/**
 * The open level's name. While the level has edits saved in this browser, a
 * modified mark (the Content Browser tile's) and a Reset that runs the same
 * undoable `level.reset` as Level › Reset to Default: a visible way back to
 * the authored level without a permanent button.
 */
export function OpenLevel() {
  const { content, dispatch, savedLevels } = useEditor();
  const { levelId } = useWorldSnapshot();
  const modified = useStore(savedLevels);
  const level = content.index.levels.find((entry) => entry.id === levelId);
  if (!level) return null;

  const edited = modified.has(level.id);
  return (
    <span className="kn-menubar__level">
      {edited && <span className="kn-menubar__modified" role="img" aria-label="Modified" />}
      <span className="kn-menubar__level-name">{level.name}.level</span>
      {edited && (
        <>
          <span className="kn-menubar__edited">Modified ·</span>
          <button
            type="button"
            className="kn-menubar__reset"
            title="Reset to Default"
            onClick={() => {
              void dispatch({ type: "level.reset", payload: { id: level.id }, origin: "menu" });
            }}
          >
            Reset
          </button>
        </>
      )}
    </span>
  );
}

/** The built-in menus, plus any contributed items appended per menu. */
export function useMenus(extra: Readonly<Record<string, readonly MenuItem[]>>): Menu[] {
  const { dispatch, links, theme: themes, savedLevels: saves } = useEditor();
  const savedLevels = useStore(saves);
  const { levelId } = useWorldSnapshot();
  const undo = useUndoState();
  const choice = useThemeChoice();
  const selection = useSelection();
  const editsFor = useEntityEdits("menu");

  const base: Menu[] = [
    {
      label: "File",
      items: [{ label: "Download Resume.pdf", href: links.resume, download: true }],
    },
    {
      label: "Edit",
      items: [
        {
          label: undo.undoLabel ? `Undo ${undo.undoLabel}` : "Undo",
          shortcut: "Ctrl+Z",
          disabled: !undo.canUndo,
          run: () => void dispatch({ type: "history.undo", payload: {}, origin: "menu" }),
        },
        {
          label: undo.redoLabel ? `Redo ${undo.redoLabel}` : "Redo",
          shortcut: "Ctrl+Shift+Z",
          disabled: !undo.canRedo,
          run: () => void dispatch({ type: "history.redo", payload: {}, origin: "menu" }),
        },
        // Unreal's Edit menu: the entity edits act on the selection.
        ...entityEditItems(editsFor(selection)),
      ],
    },
    {
      label: "Window",
      // System first (the default), then each theme; the check marks the visitor's choice.
      items: [
        { id: SYSTEM_THEME, label: "Theme: System" },
        ...themes.all.map((option) => ({
          id: option.id,
          label: `Theme: ${option.name} (${option.mode})`,
        })),
      ].map(({ id, label }) => ({
        label,
        checked: id === choice,
        run: () => void dispatch({ type: "theme.set", payload: { id }, origin: "menu" }),
      })),
    },
    {
      label: "Level",
      items: [
        {
          // Drops the open level's saved edits (undoable); enabled once it has some.
          label: "Reset to Default",
          disabled: levelId === null || !savedLevels.has(levelId),
          run: () =>
            void dispatch({ type: "level.reset", payload: { id: levelId }, origin: "menu" }),
        },
      ],
    },
    {
      label: "Help",
      items: [
        {
          label: "Tour",
          run: () => void dispatch({ type: "tour.offer", payload: {}, origin: "menu" }),
        },
        ...(links.github ? [{ label: "Source on GitHub", href: links.github }] : []),
      ],
    },
  ];

  return base.map((menu) => ({ ...menu, items: [...menu.items, ...(extra[menu.label] ?? [])] }));
}

function MenuList({ menus }: { menus: readonly Menu[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(null);
  }, []);

  useDismissOnOutsideClick(barRef, open !== null, close);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") close();
    if (open === null) return;
    if (event.key === "ArrowRight") setOpen((open + 1) % menus.length);
    if (event.key === "ArrowLeft") setOpen((open - 1 + menus.length) % menus.length);
  };

  return (
    <div className="kn-menubar__menus" role="menubar" ref={barRef} onKeyDown={onKeyDown}>
      {menus.map((menu, index) => (
        <div key={menu.label} className="kn-menubar__menu">
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open === index}
            className="kn-menubar__title"
            onClick={() => {
              setOpen(open === index ? null : index);
            }}
            onMouseEnter={() => {
              if (open !== null) setOpen(index);
            }}
          >
            {menu.label}
          </button>
          {open === index && (
            <MenuPopup
              label={menu.label}
              items={menu.items}
              onClose={close}
              className="kn-menubar__popup"
            />
          )}
        </div>
      ))}
    </div>
  );
}
